import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  MailerlitePreviewResult,
  MailerliteRunDetail,
  MailerliteRunSummary,
  MailerliteSettings,
  MailerliteStatusRefreshResult,
  MailerliteSyncResult,
  MailerliteTestResult,
  PreviewMailerliteInput,
  RunMailerliteSyncInput,
  UpdateMailerliteSettingsInput,
} from '@catalyst-psa/shared';

import { api } from '../../api/client.js';

const mailerliteKeys = {
  settings: ['mailerlite', 'settings'] as const,
  runs: ['mailerlite', 'runs'] as const,
};

export function useMailerliteSettings(): UseQueryResult<MailerliteSettings> {
  return useQuery({
    queryKey: mailerliteKeys.settings,
    queryFn: () => api.get<MailerliteSettings>('/integrations/mailerlite/settings'),
  });
}

export function useUpdateMailerliteSettings(): UseMutationResult<
  MailerliteSettings,
  unknown,
  UpdateMailerliteSettingsInput
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateMailerliteSettingsInput) =>
      api.put<MailerliteSettings>('/integrations/mailerlite/settings', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: mailerliteKeys.settings });
    },
  });
}

export function useTestMailerlite(): UseMutationResult<MailerliteTestResult, unknown, void> {
  return useMutation({
    mutationFn: () => api.post<MailerliteTestResult>('/integrations/mailerlite/settings/test', {}),
  });
}

export function usePreviewMailerlite(): UseMutationResult<
  MailerlitePreviewResult,
  unknown,
  PreviewMailerliteInput
> {
  return useMutation({
    mutationFn: (input: PreviewMailerliteInput) =>
      api.post<MailerlitePreviewResult>('/integrations/mailerlite/preview', input),
  });
}

export function useRunMailerlite(): UseMutationResult<
  MailerliteSyncResult,
  unknown,
  RunMailerliteSyncInput
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: RunMailerliteSyncInput) =>
      api.post<MailerliteSyncResult>('/integrations/mailerlite/run', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: mailerliteKeys.settings });
      void queryClient.invalidateQueries({ queryKey: mailerliteKeys.runs });
    },
  });
}

/** Re-reads subscriber statuses from MailerLite; contact lists show the mirrored value. */
export function useRefreshMailerliteStatuses(): UseMutationResult<
  MailerliteStatusRefreshResult,
  unknown,
  void
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.post<MailerliteStatusRefreshResult>('/integrations/mailerlite/refresh-statuses', {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['contacts'] });
    },
  });
}

/** Persisted run history — the audit trail for scheduled runs. */
export function useMailerliteRuns(): UseQueryResult<MailerliteRunSummary[]> {
  return useQuery({
    queryKey: mailerliteKeys.runs,
    queryFn: () => api.get<MailerliteRunSummary[]>('/integrations/mailerlite/runs'),
  });
}

/** Per-contact outcomes for one run; only fetched when a run is expanded. */
export function useMailerliteRun(id: string | null): UseQueryResult<MailerliteRunDetail> {
  return useQuery({
    queryKey: [...mailerliteKeys.runs, id],
    enabled: Boolean(id),
    queryFn: () => api.get<MailerliteRunDetail>(`/integrations/mailerlite/runs/${id!}`),
  });
}
